import styled from 'styled-components'

import { ClinTheme } from '../../../ClinTheme'
import { ClinTableRow } from '../../../components/ClinTable/ClinTable'
import { ClinPagination } from '../../../components/ClinPagination'
import { BasicSearch } from '../../../features/Search/BasicSearch'
import { ClinPageContentFrame } from '../../../components/ClinPageContentFrame'
import { ClinText } from '../../../components/ClinText'
import { mediaQueryDown } from '../../../utils/mediaQuery'

export const StyledClinTableRow = styled(ClinTableRow)({
  background: ClinTheme.colors.white
})

/**
 * AutoSuggest CSS className selectors
  container:                'react-autosuggest__container',
  containerOpen:            'react-autosuggest__container--open',
  input:                    'react-autosuggest__input',
  inputOpen:                'react-autosuggest__input--open',
  inputFocused:             'react-autosuggest__input--focused',
  suggestionsContainer:     'react-autosuggest__suggestions-container',
  suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
  suggestionsList:          'react-autosuggest__suggestions-list',
  suggestion:               'react-autosuggest__suggestion',
  suggestionFirst:          'react-autosuggest__suggestion--first',
  suggestionHighlighted:    'react-autosuggest__suggestion--highlighted',
  sectionContainer:         'react-autosuggest__section-container',
  sectionContainerFirst:    'react-autosuggest__section-container--first',
  sectionTitle:             'react-autosuggest__section-title'
 */
export const StyledAutoSuggestWrapper = styled.div({
  marginLeft: ClinTheme.space[2],
  marginRight: ClinTheme.space[3],
  position: 'relative',
  width: '100%',
  [`.react-autosuggest__container`]: {
    display: 'inline-flex',
    minWidth: '100%'
  },
  [`input`]: {
    width: '100%',
    color: ClinTheme.colors.black,
    fontSize: ClinTheme.fontSizes[2],
    fontWeight: ClinTheme.fontWeights.normal,
    lineHeight: `${ClinTheme.fontSizes[4] - 2}px`, // subtract top and bottom border 1px widths
    letterSpacing: ClinTheme.letterSpacing[0],
    padding: `${ClinTheme.space[2]}px ${ClinTheme.space[3]}px`,
    borderRadius: ClinTheme.radii[2],
    border: `solid 1px ${ClinTheme.colors.grey}`,
    transition: '.1s linear box-shadow',
    [`:focus`]: {
      outline: 'none',
      border: `solid 1px ${ClinTheme.colors.primaryMid}`,
      boxShadow: `0px 0px 3px 0px ${ClinTheme.colors.primaryMid}`
    },
    '::placeholder': {
      color: 'inherit'
    },
    ':-ms-input-placeholder': {
      // Special case for Text Inputs to have correct height in IE 11
      height: '40px'
    },
    ':disabled': {
      cursor: 'not-allowed'
    }
  },
  [`ul`]: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    backgroundColor: ClinTheme.colors.white,
    overflow: 'hidden'
  },
  [`li`]: {
    padding: `${ClinTheme.space[3]}px ${ClinTheme.space[3]}px`,
    margin: 0,
    whiteSpace: 'nowrap',
    borderBottom: `solid 1px ${ClinTheme.colors.grey}`,
    cursor: 'pointer',
    [`&:hover`]: {
      backgroundColor: ClinTheme.colors.lightGrey
    }
  },
  [`.react-autosuggest__suggestions-container`]: {
    position: 'absolute',
    left: 0,
    top: ClinTheme.space[6],
    right: 0,
    zIndex: ClinTheme.zIndices.dropdown,
    boxShadow: `0px 0px 6px 0px ${ClinTheme.colors.grey}`
  }
})
export const StyledProductSearchContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: ClinTheme.space[3]
})
export const StyledProductSearchItem = styled.div({
  backgroundColor: ClinTheme.colors.white,
  padding: '8px 16px',
  display: 'flex',
  alignItems: 'center',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: `0px 0px 6px 0px ${ClinTheme.colors.grey}`
  }
})
export const StyledProductSearchItemContentLeft = styled.div({
  display: 'flex',
  flexDirection: 'column'
})
export const StyledProductSearchPillButton = styled.div(
  {
    backgroundColor: ClinTheme.colors.lightenGreen,
    padding: '4px 15px',
    borderRadius: ClinTheme.radii[2],
    color: ClinTheme.colors.deepGreen,
    marginRight: '32px',
    fontSize: '12px',
    fontWeight: ClinTheme.fontWeights.medium,
    lineHeight: '16px',
    minWidth: '122px',
    textAlign: 'center'
  },
  ({ isProgram }: { isProgram: boolean }) => ({
    color: isProgram ? ClinTheme.colors.primaryMid : ClinTheme.colors.deepGreen,
    backgroundColor: isProgram
      ? ClinTheme.colors.deliveryDateBackground
      : ClinTheme.colors.lightenGreen
  })
)
export const StyledProductSearchItemContentRight = styled.div({
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto'
})
export const StyledProductSearchPagination = styled(ClinPagination)({
  margin: '0 auto',
  maxWidth: '1140px',
  width: '100%',
  paddingTop: '40px',
  fontFamily: ClinTheme.fonts.body,
  '& .pagination-container': {
    flexGrow: 0,
    fontSize: ClinTheme.fontSizes[1],
    fontWeight: ClinTheme.fontWeights.medium,
    lineHeight: '16px',
    '& > div > span': {
      fontSize: ClinTheme.fontSizes[1],
      lineHeight: '16px'
    }
  },
  '& .pagination-previous-button': {
    fontSize: ClinTheme.fontSizes[1],
    fontWeight: ClinTheme.fontWeights.medium,
    lineHeight: '16px',
    fontFamily: ClinTheme.fonts.body,
    marginLeft: '0',
    paddingRight: '26px'
  },
  '& .pagination-next-button': {
    fontSize: ClinTheme.fontSizes[1],
    fontWeight: ClinTheme.fontWeights.medium,
    lineHeight: '16px',
    fontFamily: ClinTheme.fonts.body,
    paddingLeft: '26px'
  },
  '& .pagination-select': {
    '& > label': {
      width: '64px'
    },
    '& > span': {
      margin: '0 8px'
    }
  },
  '& .pagination-results-per-page': {
    flex: 0
  }
})
export const StyledProductSearchHeader = styled.div({
  display: 'flex',
  fontFamily: ClinTheme.fonts.groteskRegular,
  alignItems: 'center',
  padding: '24px 0'
})
export const StyledPageTitle = styled.div({
  fontFamily: ClinTheme.fonts.groteskExtraBold,
  fontSize: '32px',
  fontWeight: ClinTheme.fontWeights.bolder,
  marginRight: '24px'
})
export const StyledBasicSearch = styled(BasicSearch)({
  width: '100%',
  maxWidth: '565px',
  '& > input': {
    borderRadius: '20px',
    color: ClinTheme.colors.black
  }
})
export const StyledProductSearchFilterContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: ClinTheme.space[2],
  padding: '24px 0',
  marginBottom: '32px',
  borderBottom: `solid 1px ${ClinTheme.colors.grey}`
})
export const StyledProductSearchCounter = styled.div({
  position: 'absolute',
  right: '-36px',
  top: '0',
  backgroundColor: '#EDEDFC',
  color: '#5E5ADB',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  width: '23px',
  fontSize: '10px',
  height: '21px',
  display: 'flex',
  fontWeight: ClinTheme.fontWeights.medium
})
export const SearchPageHeader = styled.div({
  backgroundColor: ClinTheme.colors.white
})
export const SearchPageContent = styled.div({
  backgroundColor: ClinTheme.colors.lightGrey
})
export const StyledHeaderClinPageContentFrame = styled(ClinPageContentFrame)({
  marginBottom: '0px !important'
})
export const StyledProductBrandText = styled(ClinText)({
  width: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  maxWidth: '790px',
  textOverflow: 'ellipsis',
  margin: 0,
  lineHeight: '24px',
  color: ClinTheme.colors.darkGrey,
  fontSize: ClinTheme.fontSizes[1],
  [`@media screen and (max-width: ${ClinTheme.breakpoints[2]}px)`]: {
    whiteSpace: 'normal'
  }
})

export const StyledAlternativesMessage = styled.div({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: ClinTheme.colors.white,
  borderRadius: ClinTheme.radii[2],
  padding: `${ClinTheme.space[3]}px`,
  marginBottom: `${ClinTheme.space[4]}px`,
  border: `1px solid transparent`,
  '& svg': {
    marginRight: ClinTheme.space[2]
  }
})
const MediumUpBreakpoint = ClinTheme.breakpoints[1]

export const StyledFilterWrapper = styled.div({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  [mediaQueryDown(MediumUpBreakpoint)]: {
    flexDirection: 'column',
    alignItems: 'flex-start'
  }
})

export const StyledFilter = styled.div({
  display: 'flex',
  alignItems: 'center',
  position: 'relative',
  flexWrap: 'wrap',
  gap: `${ClinTheme.space[2]}px`
})
